import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteGPanelContacts,
  GPanelContactsKeys,
} from '../../gpanel-contacts-api.ts';
import ChangeLogDialog from '../../../../../shared/change-log-dialog.tsx';
import { useState } from 'react';
import { Messages } from '../../../../../utils/message-constants.ts';
import axios from 'axios';
import { ApiException } from '../../../../../../types.ts';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list.ts';
import { useAnalytics } from '../../../../../shared/analytics/create-events.ts';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../utils/roles/client-roles.ts';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import { handleErrors } from '../../../../../utils/errors.ts';
import { useStore } from '../../../../state/zustand-store.ts';
import { Contact, GPanelContactsDeletePageProps } from '../../shared/types.ts';
import SharedDialog from '../../../../../shared/shared-dialog.tsx';

import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export default function GPanelContactsDeleteDialog({
  handleClose,
  contactIds,
}: GPanelContactsDeletePageProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const { changeLogRoles } = useUserSettings();
  const { setSnackbar } = useStore();

  const isBulkDelete = contactIds.length > 1;

  const contacts =
    queryClient.getQueryData<Contact[]>([
      GPanelContactsKeys.OrganizationDirectoryContacts,
    ]) ?? [];

  const contact =
    contactIds.length === 1
      ? contacts.find((c) => Number(c.id) === contactIds[0])
      : null;

  const contactName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
      contact.emails?.[0]?.value
    : 'the contact';

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteGPanelContacts(contactIds, changeLogMessage),
    onSuccess: () => {
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: contactIds.length > 25 ? 'info' : 'success',
        message:
          contactIds.length > 25
            ? 'Deleting multiple contacts in progress.'
            : Messages.Success.Trashed('Contact'),
      });
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.LabelGroups],
      });
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.OrganizationDirectoryContacts],
      });
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.TrashContacts],
      });
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Trashing('contact'),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.trash_contact, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleDelete = async () => {
    if (
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.TrashContact.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  };

  return (
    <>
      <SharedDialog
        open
        handleClose={handleClose}
        title={'Move Contact' + (isBulkDelete ? 's' : '') + ' to Trash'}
        dialogContent={
          <Stack>
            <Typography>
              Move{' '}
              {isBulkDelete ? (
                <>
                  <strong>{contactIds.length}</strong> contacts
                </>
              ) : (
                <strong>{contactName}</strong>
              )}{' '}
              to trash? This will remove contact{isBulkDelete ? 's' : ''} from
              all labels and will be permanently deleted after 30 days.
            </Typography>
            {errorText && <FormHelperText error children={errorText} />}
          </Stack>
        }
        primaryButton={{
          loading: isPending,
          disabled: isPending,
          action: handleDelete,
          color: 'warning',
          title: 'Move to Trash',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
